import {
    MAX_TOTAL_SIZE_BYTES,
    MAX_SINGLE_FILE_SIZE_BYTES,
    MAX_FILE_COUNT
} from '../constants/uploadConfig';
import { SYSTEM_FILES, SKIP_CATEGORIES, UPLOAD_LIMITS, UPLOAD_LIMIT_TYPES } from '../constants/fileValidation';
import { formatFileSizeInGB } from './formatUtils';
import { resolveFileExtension } from './fileExtensionUtils';


export { formatFileSizeInGB } from './formatUtils';


/**
 * Checks if a filename contains any disallowed characters.
 * @param {string} filename
 * @param {string[]} disallowedChars - Array of disallowed characters
 * @returns {boolean}
 */
export function hasDisallowedChars(filename, disallowedChars) {
    if (!Array.isArray(disallowedChars) || disallowedChars.length === 0) {
        return false; 
    }
    return disallowedChars.some((ch) => filename.includes(ch));
}


/**
 * Returns the disallowed characters present in a filename.
 * @param {string} filename
 * @param {string[]} disallowedChars - Array of disallowed characters
 * @returns {string[]}
 */
export function getDisallowedChars(filename, disallowedChars) {
    if (!Array.isArray(disallowedChars)) return [];
    return disallowedChars.filter((ch) => filename.includes(ch));
}


/**
 * Checks for files with unsupported (disallowed) filename characters.
 * @param {File[]} files - Array of File objects
 * @param {string[]} disallowedChars - Array of disallowed characters
 * @returns {Array} Array of file names with unsupported characters
 */
export function getFilesWithUnsupportedNames(files, disallowedChars) {
    if (!Array.isArray(files) || !Array.isArray(disallowedChars)) return [];
    return files
        .filter(file => hasDisallowedChars(file.name, disallowedChars))
        .map(file => file.name);
}
/**
 * File Validation Utilities
 * 
 * Provides helper functions for file validation, formatting, and notification message building
 */

/**
 * Checks if a file is a system/metadata file that should be ignored
 * @param {string} fileName - Name of the file to check
 * @returns {boolean} True if file should be ignored
 */
export const isSystemFile = (fileName) => {
    return SYSTEM_FILES.has(fileName);
};

/**
 * Truncates a file list to show only first N items with "and X more" suffix
 * @param {Array} fileList - Array of file names/strings
 * @param {number} maxToShow - Maximum number of items to show (default: 3)
 * @returns {string} Formatted file list string
 */
export const truncateFileList = (fileList, maxToShow = 3) => {
    const visibleFiles = fileList.slice(0, maxToShow).join(', ');
    const remainingCount = fileList.length - maxToShow;
    return remainingCount > 0 
        ? `${visibleFiles} and ${remainingCount} more` 
        : visibleFiles;
};
/**
 * Returns only categories that have at least one skipped file,
 * with the files array attached.
 */
const getActiveCategories = (skippedFiles) => {
    const { large = [], zeroSize = [], invalidExtension = [], undetectedType = [] } = skippedFiles;
    const allCategories = { large, zeroSize, invalidExtension, undetectedType };
    return SKIP_CATEGORIES
        .map(cat => ({ ...cat, files: allCategories[cat.key] }))
        .filter(cat => cat.files.length > 0);
};

/**
 * Builds a skip notification message based on validation results
 * @param {Object} skippedFiles - Object containing arrays of skipped files by type
 * @param {Array} skippedFiles.large - Files exceeding size limit (with formatted sizes)
 * @param {Array} skippedFiles.zeroSize - Zero-byte files
 * @param {Array} skippedFiles.invalidExtension - Files with invalid extensions
 * @param {boolean} allSkipped - Whether all files were skipped
 * @returns {string} Formatted notification message
 */
export const buildSkipMessage = (skippedFiles, allSkipped = false) => {
    const active = getActiveCategories(skippedFiles);
    const totalSkipped = active.reduce((sum, cat) => sum + cat.files.length, 0);

    if (totalSkipped === 0) return '';

    // If all files were skipped, show summary message
    if (allSkipped) {
        const skipCounts = active.map(cat => cat.countLabel(cat.files.length));
        return `Cannot upload: All ${totalSkipped} selected file(s) were skipped (${skipCounts.join(', ')})`;
    }

    // Single skip type - show simple message
    if (active.length === 1) {
        const { files, singleMsg } = active[0];
        return singleMsg(files.length, truncateFileList(files));
    }

    // Multiple skip types - show combined multiline message
    const lines = ['Skipped files:'];
    active.forEach(cat => lines.push(cat.multiLine(cat.files.length, truncateFileList(cat.files))));
    return lines.join('\n');
};

/**
 * Validates files and returns validation results with categorized skipped files
 * @param {File[]} files - Array of File objects to validate
 * @param {Object} validators - Validation functions and limits
 * @param {number} validators.maxSizeBytes - Maximum file size in bytes
 * @param {Function} validators.isExtensionAllowed - Function to check if extension is allowed
 * @param {Function} validators.getFileExtension - Function to get file extension
 * @returns {Object} Validation results
 */
export const validateFiles = async (files, validators) => {
    const { isExtensionAllowed, getFileExtension } = validators;
    
    const skippedLargeFiles = [];
    const skippedZeroSize = [];
    const skippedInvalidExtension = [];
    const skippedUndetectedType = [];
    
    const validFiles = [];
    
    for (const file of files) {
        // Silently skip system/metadata files (no notifications)
        if (isSystemFile(file.name)) {
            console.debug(`🔇 Silently ignoring system file: ${file.name}`);
            continue;
        }
        
        // Skip files exceeding size limit
        // TODO: Uncomment to enable individual file size limit
        // if (file.size > maxSizeBytes) {
        //     skippedLargeFiles.push(`${file.name} (${formatFileSizeInGB(file.size)})`);
        //     console.warn(`⚠️ Skipping file exceeding ${MAX_FILE_SIZE_GB}GB: ${file.name} - ${formatFileSizeInGB(file.size)}`);
        //     return false;
        // }
        
        // Skip zero-byte files
        if (file.size === 0) {
            skippedZeroSize.push(file.name);
            console.warn(`⚠️ Skipping zero-byte file: ${file.name}`);
            continue;
        }
        
        // Skip files with disallowed extensions (for files that have an extension)
        if (!isExtensionAllowed(file.name)) {
            const ext = getFileExtension(file.name);
            skippedInvalidExtension.push(`${file.name} (${ext || 'no extension'})`);
            console.warn(`⚠️ Skipping file with invalid extension: ${file.name}`);
            continue;
        }
        
        // For extensionless files, detect file type and skip if undetectable
        const lastDotIndex = file.name.lastIndexOf('.');
        if (lastDotIndex <= 0) {
            const detectedExt = await resolveFileExtension(file.name, file);
            if (!detectedExt) {
                skippedUndetectedType.push(file.name);
                console.warn(`⚠️ Skipping file with undetected file type: ${file.name}`);
                continue;
            }
        }
        
        validFiles.push(file);
    }
    
    return {
        validFiles,
        skippedFiles: {
            large: skippedLargeFiles,
            zeroSize: skippedZeroSize,
            invalidExtension: skippedInvalidExtension,
            undetectedType: skippedUndetectedType
        },
        totalSkipped: skippedLargeFiles.length + skippedZeroSize.length + skippedInvalidExtension.length + skippedUndetectedType.length
    };
};

/**
 * Validates files against upload limits (total size, individual file size, file count)
 * @param {File[]} files - Array of File objects to validate
 * @returns {Object} Validation result with exceeded limits and details
 */
export const validateUploadLimits = (files) => {
    const result = {
        isValid: true,
        exceededLimits: [],
        totalSize: 0,
        totalFiles: files.length,
        largestFile: null,
        largestFileSize: 0
    };
    
    // Calculate total size and find largest file
    files.forEach(file => {
        result.totalSize += file.size;
        if (file.size > result.largestFileSize) {
            result.largestFileSize = file.size;
            result.largestFile = file.name;
        }
    });
    
    // Check total file count
    if (result.totalFiles > MAX_FILE_COUNT) {
        const def = UPLOAD_LIMITS[UPLOAD_LIMIT_TYPES.FILE_COUNT];
        result.isValid = false;
        result.exceededLimits.push({
            type: def.type,
            limit: def.limit,
            actual: result.totalFiles,
            message: def.buildMessage({ totalFiles: result.totalFiles })
        });
    }

    // Check total dataset size
    if (result.totalSize > MAX_TOTAL_SIZE_BYTES) {
        const def = UPLOAD_LIMITS[UPLOAD_LIMIT_TYPES.TOTAL_SIZE];
        result.isValid = false;
        result.exceededLimits.push({
            type: def.type,
            limit: def.limit,
            actual: result.totalSize,
            message: def.buildMessage({ formattedSize: formatFileSizeInGB(result.totalSize) })
        });
    }

    // Check individual file size
    if (result.largestFileSize > MAX_SINGLE_FILE_SIZE_BYTES) {
        const def = UPLOAD_LIMITS[UPLOAD_LIMIT_TYPES.SINGLE_FILE_SIZE];
        result.isValid = false;
        result.exceededLimits.push({
            type: def.type,
            limit: def.limit,
            actual: result.largestFileSize,
            file: result.largestFile,
            message: def.buildMessage({
                fileName: result.largestFile,
                formattedSize: formatFileSizeInGB(result.largestFileSize)
            })
        });
    }

    return result;
};
